import {
  isArrangementLocked,
  isConsultationCompleteForStage,
} from "helpers/at-need";

export type StartHubTaskStatus = "start" | "resume" | "done" | "disabled";

/**
 * Resolved completion signal for a single Task System task, merged with the
 * optimistic latch: "completed" once the API confirms it or a latch is
 * active for it, "open" when the task exists and isn't done, "unavailable"
 * when there's no task data at all (rootless case, or a v2-era root that
 * never had this task) — callers fall back to legacy per-domain signals.
 */
export type HubTaskState = "completed" | "open" | "unavailable";

export type StartHubLocationState = {
  consultationBooked?: boolean;
  showCompletedStartHub?: boolean;
  skipStartHub?: boolean;
} | null;

function getStartHubLocationState(state: unknown): StartHubLocationState {
  if (!state || typeof state !== "object") return null;
  return state as StartHubLocationState;
}

export function shouldShowCompletedStartHubFromState(state: unknown): boolean {
  return getStartHubLocationState(state)?.showCompletedStartHub === true;
}

export function hasBookedConsultationFromState(state: unknown): boolean {
  return getStartHubLocationState(state)?.consultationBooked === true;
}

export function shouldSkipStartHubFromState(state: unknown): boolean {
  return getStartHubLocationState(state)?.skipStartHub === true;
}

export function getVitalsHubStatus({
  hasSubmission,
  taskState,
}: {
  hasSubmission: boolean;
  taskState: HubTaskState;
}): StartHubTaskStatus {
  if (taskState === "completed") return "done";
  if (taskState === "open") return "start";
  // "unavailable": no task data for this case — fall back to the legacy
  // submission-based signal.
  return hasSubmission ? "done" : "start";
}

export function getArrangementHubStatus(
  arrangementStatus: string | null | undefined,
  taskState: HubTaskState,
): StartHubTaskStatus {
  if (taskState === "completed") return "done";
  if (taskState === "open") {
    return arrangementStatus === "draft" ? "resume" : "start";
  }
  // "unavailable": legacy logic based on the domain arrangement status alone.
  if (isArrangementLocked(arrangementStatus)) return "done";
  if (arrangementStatus) return "resume";
  return "start";
}

export function hasScheduledConsultation(appData: unknown): boolean {
  const consultation = (
    appData as
      { properties?: { consultation?: { status?: string | null } } } | undefined
  )?.properties?.consultation;

  if (consultation?.status === "cancelled") return false;
  return consultation?.status === "scheduled";
}

export function getConsultationHubStatus({
  hasConsultationBooking,
  vitalsStatus,
  arrangementStatus,
  stage,
  taskState,
}: {
  hasConsultationBooking: boolean;
  vitalsStatus: StartHubTaskStatus;
  arrangementStatus: StartHubTaskStatus;
  stage: string | null | undefined;
  taskState: HubTaskState;
}): StartHubTaskStatus {
  if (taskState === "completed") return "done";
  if (taskState === "open") {
    return vitalsStatus === "done" && arrangementStatus === "done"
      ? "start"
      : "disabled";
  }
  // "unavailable": legacy logic — a v2-era root has no schedule_consultation
  // task, and a rootless case has no task data at all.
  if (hasConsultationBooking || isConsultationCompleteForStage(stage))
    return "done";
  if (vitalsStatus === "done" && arrangementStatus === "done") return "start";
  return "disabled";
}

export function shouldShowStartHub({
  isCareSpecialist,
  arrangementFlowEnabled,
  vitalsStatus,
  arrangementStatus,
  consultationStatus,
}: {
  isCareSpecialist: boolean;
  arrangementFlowEnabled: boolean;
  vitalsStatus: StartHubTaskStatus;
  arrangementStatus: StartHubTaskStatus;
  consultationStatus: StartHubTaskStatus;
}): boolean {
  if (isCareSpecialist || !arrangementFlowEnabled) return false;

  return !(
    vitalsStatus === "done" &&
    arrangementStatus === "done" &&
    consultationStatus === "done"
  );
}

export function areStartHubItemsDone({
  vitalsStatus,
  arrangementStatus,
  consultationStatus,
}: {
  vitalsStatus: StartHubTaskStatus;
  arrangementStatus: StartHubTaskStatus;
  consultationStatus: StartHubTaskStatus;
}): boolean {
  return (
    vitalsStatus === "done" &&
    arrangementStatus === "done" &&
    consultationStatus === "done"
  );
}
